import React, { FC, useState } from 'react';
import DisplayAsset from '../../display_asset';
import { 
    NHAssetType,
    IPortalPropertyPhotoObj,
    IPortalPropertyTagObj
} from '../../../../interfaces/asset';
import ProfileCompletenessText from '../../../profile_completeness/profile_completeness_text';
import { ProfileScoreType } from '../../../../interfaces/property';
import { TagKeyNameIcon } from '../../../tag_keyname_icon';
import { StarredAssetToggle } from './starred_asset_toggle';
import { TagFilterPill } from './tag_filter_pill';
import { AddPhotosButton } from './add_photos_button';
import { UpdatePhotosButton } from './update_photos_button';

interface IProps {
    property: any, 
    onPanelSelected: (nhID: number) => void
}

const PropertyPhotoPanel: FC<IProps> = ({property, onPanelSelected}) => {
    const { WebsiteURL, StateSlug, CitySlug, Slug, Name, Address, NHID } = property.Property;
    const [tagFilter, setTagFilter] = useState<any>(null);
    const tagMatchesAppliedFilter = (tagID: number) => tagFilter === tagID;
    const isPropertyTagFilterApplied = () => tagFilter !== null;

    const renderPhoto = (photo: IPortalPropertyPhotoObj, nhID: number) => {   
        return (
            <div className="relative">
                <span className='absolute bottom-3 right-3 h-8 leading-8 px-3 bg-brand_secondary whitespace-nowrap text-2xs text-white rounded-full'>
                    {photo.TagName}
                </span>
                <StarredAssetToggle 
                    nhID={nhID} 
                    assetID={photo.AssetID} 
                    assetTypeID={NHAssetType.Photos} 
                    isStarred={photo.IsStarred} 
                />
                <DisplayAsset
                    className='object-cover rounded-md min-w-72 max-w-72 h-44'
                    TransformedAssetUrl={photo.TransformedAssetUrl}
                    FileName={photo.FileName}
                    AssetType={NHAssetType.Photos} 
                />
            </div>

        );
    };

    return (
        <div className="ring-1 ring-brand_grey overflow-hidden rounded-md lg:rounded-lg flex flex-col" key={Name}>
            <div className="bg-brand_faint-blue px-3 sm:px-4 2xl:px-5 py-3 min-h-14 flex flex-col items-start xl:items-center xl:justify-between gap-2 xl:flex-row h-full">
                <div className="flex flex-wrap gap-y-1 items-center gap-x-2 w-full [&>em]:w-full">
                    <h4 className="font-semibold leading-tight text-lg lg:text-xl">
                        {Name}
                    </h4>
                    <span className="inline-block text-xs lg:text-sm leading-tight text-brand_grey-medium">
                        {Address}
                    </span>
                    <a
                        className="link text-xs bg-white px-2 py-1 inline-block leading-none rounded-lg"
                        href={`${WebsiteURL}/${StateSlug}/${CitySlug}/${Slug}`}
                        target="_blank" 
                    >
                        view on website
                    </a>  
                    <ProfileCompletenessText
                        NHID={NHID}
                        profileScoreType={ProfileScoreType.Photo}
                    />
                </div>
                <div className="flex flex-wrap gap-2 xl:ms-auto xl:flex-row xl:flex-nowrap">
                    <AddPhotosButton 
                        propertyNhID={NHID} 
                        propertyName={Name} 
                        propertySlug={property?.Property?.Slug}
                    />
                    { property.Photo.length ? <UpdatePhotosButton onClick={() => onPanelSelected(NHID)}/> : null }
                </div>
            </div>
            <div>
                { property.Photo.length ?
                    <>
                        <ul className="p-3 sm:p-4 2xl:p-5 flex flex-wrap gap-2.5">
                            <li key={`show-property-photos-${NHID}-tag-all`}>
                                <TagFilterPill 
                                    text='All Photos'
                                    isActive={tagMatchesAppliedFilter(null)} 
                                    onClick={() => setTagFilter(null)}   
                                    itemCount={property.Photo.length}
                                />
                            </li>
                            {property.Tag.map((tag: IPortalPropertyTagObj) => {
                                return <li key={`show-property-photos-${NHID}-tag-${tag.TagID}`}>
                                    <TagFilterPill  
                                        icon={<TagKeyNameIcon tagKeyName={tag.TagKeyName} className="text-lg" />}
                                        text={tag.TagName}
                                        isActive={tagMatchesAppliedFilter(tag.TagID)} 
                                        onClick={() => setTagFilter(tag.TagID)}   
                                        itemCount={tag.Photo.length}
                                    />
                                </li>;
                            })}
                        </ul>

                        <ul className="px-3 sm:px-4 2xl:px-5 pb-3 sm:pb-4 2xl:pb-5 gap-3 flex overflow-x-auto scrollbar scrollbar-full scrollbar-breadcrumbs">
                            {
                                property.Photo.map((photo: IPortalPropertyPhotoObj) => {
                                    const {NHID} = property.Property;
                                    const {TagID, AssetID } = photo;
                                    const shouldShowPhoto = tagMatchesAppliedFilter(TagID) || !isPropertyTagFilterApplied();
                                    return (
                                        shouldShowPhoto && (
                                            <li key={AssetID} className='bg-white relative'>
                                                { renderPhoto(photo, NHID) }
                                            </li>
                                        ) 
                                    );
                                })}
                        </ul>
                    </> :
                    <span className="msg msg-warning">
                        No photo uploaded
                    </span>
                }
            </div>
        </div>
    );
};

export { PropertyPhotoPanel};